import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiResult } from "@/lib/apiClient";
import { queryKeys } from "@/lib/queryKeys";
import {
    UpdateSettingsDto,
    UpdateAppearanceDto,
    UpdateSeoDto,
    UpdateProfileDto,
    UpdatePersonalInfoDto,
    AddSocialLinkDto,
    FirstJobShareResponseDto,
    FirstPostStudioShareResponseDto,
    BioLinkAddedResponseDto,
    JobShareClickResponseDto,
    JobShareSuccessResponseDto,
    UpdateSettingsSchema,
    UpdateAppearanceSchema,
    UpdateSeoSchema,
    UpdateProfileSchema,
    UpdatePersonalInfoSchema,
    AddSocialLinkSchema,
    FirstJobShareSchema,
    FirstPostStudioShareSchema,
    BioLinkAddedSchema,
    JobShareClickSchema,
    JobShareSuccessSchema,
} from "@artha/api-contracts";
import { CreatorProfileForMyLink, SocialProvider, API_ROUTES } from "@artha/types";
import { useState, useEffect } from "react";

type ApiPayload<T> = T | { data: T };

const unwrapApiData = <T>(res: ApiResult<ApiPayload<T>>): T => {
    const payload = res.data as ApiPayload<T>;
    if (payload && typeof payload === "object" && "data" in (payload as Record<string, unknown>)) {
        return (payload as { data: T }).data;
    }
    return payload as T;
};

export const fetchMyLinkProfile = async (): Promise<CreatorProfileForMyLink> => {
    const res = await apiFetch<ApiPayload<CreatorProfileForMyLink>>("/creators/me/my-link-profile");

    if (!res.ok) {
        throw new Error("Failed to fetch My Link profile");
    }

    return unwrapApiData(res);
};

export const useMyLinkProfileQuery = (options?: { enabled?: boolean }) =>
    useQuery<CreatorProfileForMyLink>({
        queryKey: queryKeys.myLinkProfile(),
        queryFn: fetchMyLinkProfile,
        ...options,
    });

export const updateSettings = async (dto: UpdateSettingsDto): Promise<CreatorProfileForMyLink> => {
    const body = UpdateSettingsSchema.parse(dto);

    const res = await apiFetch<ApiPayload<CreatorProfileForMyLink>>("/creators/me/settings", {
        method: "PATCH",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to update settings");
    }

    return unwrapApiData(res);
};

export const useUpdateSettingsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateSettings,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
            queryClient.invalidateQueries({ queryKey: queryKeys.jobs() });
        },
    });
};

export const updateAppearance = async (dto: UpdateAppearanceDto): Promise<CreatorProfileForMyLink> => {
    const body = UpdateAppearanceSchema.parse(dto);

    const res = await apiFetch<ApiPayload<CreatorProfileForMyLink>>("/creators/me/appearance", {
        method: "PATCH",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to update appearance");
    }

    return unwrapApiData(res);
};

export const markFirstJobShare = async (): Promise<FirstJobShareResponseDto> => {
    const body = FirstJobShareSchema.parse({});

    const res = await apiFetch<ApiPayload<FirstJobShareResponseDto>>(API_ROUTES.CREATORS.ANALYTICS.FIRST_JOB_SHARE, {
        method: "POST",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to track first job share");
    }

    return unwrapApiData(res);
};

export const useMarkFirstJobShareMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: markFirstJobShare,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

export const markFirstPostStudioShare = async (): Promise<FirstPostStudioShareResponseDto> => {
    const body = FirstPostStudioShareSchema.parse({});

    const res = await apiFetch<ApiPayload<FirstPostStudioShareResponseDto>>(API_ROUTES.CREATORS.ANALYTICS.FIRST_POST_STUDIO_SHARE, {
        method: "POST",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to track first post studio share");
    }

    return unwrapApiData(res);
};

export const useMarkFirstPostStudioShareMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: markFirstPostStudioShare,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

export const markBioLinkAdded = async (): Promise<BioLinkAddedResponseDto> => {
    const body = BioLinkAddedSchema.parse({});

    const res = await apiFetch<ApiPayload<BioLinkAddedResponseDto>>(API_ROUTES.CREATORS.ANALYTICS.BIO_LINK_ADDED, {
        method: "POST",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to mark bio link as added");
    }

    return unwrapApiData(res);
};

export const useMarkBioLinkAddedMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: markBioLinkAdded,
        onSuccess: () => {
            queryClient.setQueryData<CreatorProfileForMyLink>(
                queryKeys.myLinkProfile(),
                (profile) => profile
                    ? { ...profile, bioLinkAddedAt: profile.bioLinkAddedAt ?? new Date().toISOString() }
                    : profile,
            );
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

export const trackJobShareClick = async (): Promise<JobShareClickResponseDto> => {
    const body = JobShareClickSchema.parse({});

    const res = await apiFetch<ApiPayload<JobShareClickResponseDto>>(API_ROUTES.CREATORS.ANALYTICS.JOB_SHARE_CLICK, {
        method: "POST",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to track job share click");
    }

    return unwrapApiData(res);
};

export const markJobShareSuccess = async (): Promise<JobShareSuccessResponseDto> => {
    const body = JobShareSuccessSchema.parse({});

    const res = await apiFetch<ApiPayload<JobShareSuccessResponseDto>>(API_ROUTES.CREATORS.ANALYTICS.JOB_SHARE_SUCCESS, {
        method: "POST",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to track job share success");
    }

    return unwrapApiData(res);
};

export const useUpdateAppearanceMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateAppearance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

// SEO Update
export const updateSeo = async (dto: UpdateSeoDto): Promise<CreatorProfileForMyLink> => {
    const body = UpdateSeoSchema.parse(dto);

    const res = await apiFetch<ApiPayload<CreatorProfileForMyLink>>("/creators/me/seo", {
        method: "PATCH",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to update SEO settings");
    }

    return unwrapApiData(res);
};

export const useUpdateSeoMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateSeo,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

// Profile Update (displayName, bio)
export const updateProfile = async (dto: UpdateProfileDto): Promise<CreatorProfileForMyLink> => {
    const body = UpdateProfileSchema.parse(dto);

    const res = await apiFetch<ApiPayload<CreatorProfileForMyLink>>("/creators/me/profile", {
        method: "PATCH",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to update profile");
    }

    return unwrapApiData(res);
};

export const useUpdateProfileMutation = () => {
    const queryClient = useQueryClient();

    return useMutation<CreatorProfileForMyLink, Error, UpdateProfileDto>({
        mutationFn: updateProfile,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

// Avatar Upload (FormData)
export const uploadAvatar = async (file: File): Promise<{ avatarUrl: string }> => {
    const formData = new FormData();
    formData.append("avatar", file);

    const res = await apiFetch<ApiPayload<{ avatarUrl: string }>>("/creators/me/avatar", {
        method: "POST",
        body: formData,
    });

    if (!res.ok) {
        throw new Error("Failed to upload avatar");
    }

    return unwrapApiData(res);
};

export const useUploadAvatarMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: uploadAvatar,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

// Social Links
export const addSocialLink = async (dto: AddSocialLinkDto): Promise<CreatorProfileForMyLink> => {
    const body = AddSocialLinkSchema.parse(dto);

    const res = await apiFetch<ApiPayload<CreatorProfileForMyLink>>("/creators/me/social-links", {
        method: "POST",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to add social link");
    }

    return unwrapApiData(res);
};

export const useAddSocialLinkMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: addSocialLink,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

export const removeSocialLink = async (platform: SocialProvider): Promise<CreatorProfileForMyLink> => {
    const res = await apiFetch<ApiPayload<CreatorProfileForMyLink>>(`/creators/me/social-links/${platform}`, {
        method: "DELETE",
    });

    if (!res.ok) {
        throw new Error("Failed to remove social link");
    }

    return unwrapApiData(res);
};

export const useRemoveSocialLinkMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: removeSocialLink,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

// Username availability check
export const checkUsername = async (username: string): Promise<{ available: boolean }> => {
    const res = await apiFetch<ApiPayload<{ available: boolean }>>(`/creators/me/check-username?username=${encodeURIComponent(username)}`);

    if (!res.ok) {
        throw new Error("Failed to check username");
    }

    return unwrapApiData(res);
};

/**
 * Hook for debounced username availability check
 * @param username The username to check
 * @param currentUsername The user's current username (to skip check if unchanged)
 * @param debounceMs Debounce delay in milliseconds (default 500ms)
 */
export const useUsernameAvailability = (
    username: string,
    currentUsername: string,
    debounceMs = 500
) => {
    const [debouncedUsername, setDebouncedUsername] = useState(username);
    const [isChecking, setIsChecking] = useState(false);
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Debounce the username
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedUsername(username);
        }, debounceMs);
        return () => clearTimeout(timer);
    }, [username, debounceMs]);

    // Check availability when debounced username changes
    useEffect(() => {
        const trimmed = debouncedUsername.trim();
        
        // Reset if empty or same as current
        if (!trimmed || trimmed === currentUsername) {
            setIsAvailable(null);
            setError(null);
            return;
        }

        // Validate format
        if (trimmed.length < 3) {
            setError("Username must be at least 3 characters");
            setIsAvailable(null);
            return;
        }
        if (trimmed.length > 30) {
            setError("Username must be at most 30 characters");
            setIsAvailable(null);
            return;
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
            setError("Username can only contain letters, numbers, underscores, and hyphens");
            setIsAvailable(null);
            return;
        }

        setIsChecking(true);
        setError(null);

        checkUsername(trimmed)
            .then((result) => {
                setIsAvailable(result.available);
                if (!result.available) {
                    setError("Username is already taken");
                }
            })
            .catch(() => {
                setError("Failed to check username");
                setIsAvailable(null);
            })
            .finally(() => {
                setIsChecking(false);
            });
    }, [debouncedUsername, currentUsername]);

    return { isChecking, isAvailable, error };
};

// Personal Info Update
export const updatePersonalInfo = async (dto: UpdatePersonalInfoDto): Promise<CreatorProfileForMyLink> => {
    const body = UpdatePersonalInfoSchema.parse(dto);

    const res = await apiFetch<ApiPayload<CreatorProfileForMyLink>>("/creators/me/personal-info", {
        method: "PATCH",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        throw new Error("Failed to update personal information");
    }

    return unwrapApiData(res);
};

export const useUpdatePersonalInfoMutation = () => {
    const queryClient = useQueryClient();

    return useMutation<CreatorProfileForMyLink, Error, UpdatePersonalInfoDto>({
        mutationFn: updatePersonalInfo,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.myLinkProfile() });
        },
    });
};

// Email availability check
export const checkEmailAvailability = async (email: string): Promise<{ available: boolean }> => {
    const res = await apiFetch<ApiPayload<{ available: boolean }>>(`/creators/me/check-email?email=${encodeURIComponent(email)}`);

    if (!res.ok) {
        throw new Error("Failed to check email availability");
    }

    return unwrapApiData(res);
};

/**
 * Hook for debounced email availability check
 * @param email The email to check
 * @param currentEmail The user's current email (to skip check if unchanged)
 * @param debounceMs Debounce delay in milliseconds (default 500ms)
 */
export const useEmailAvailability = (
    email: string,
    currentEmail: string,
    debounceMs = 500
) => {
    const [debouncedEmail, setDebouncedEmail] = useState(email);
    const [isChecking, setIsChecking] = useState(false);
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Debounce the email
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedEmail(email);
        }, debounceMs);
        return () => clearTimeout(timer);
    }, [email, debounceMs]);

    // Check availability when debounced email changes
    useEffect(() => {
        const trimmed = debouncedEmail.trim().toLowerCase();
        
        // Reset if empty or same as current
        if (!trimmed || trimmed === currentEmail.toLowerCase()) {
            setIsAvailable(null);
            setError(null);
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmed)) {
            setError("Please enter a valid email address");
            setIsAvailable(null);
            return;
        }

        if (trimmed.length > 255) {
            setError("Email must be at most 255 characters");
            setIsAvailable(null);
            return;
        }

        setIsChecking(true);
        setError(null);

        checkEmailAvailability(trimmed)
            .then((result) => {
                setIsAvailable(result.available);
                if (!result.available) {
                    setError("Email is already in use");
                }
            })
            .catch(() => {
                setError("Failed to check email availability");
                setIsAvailable(null);
            })
            .finally(() => {
                setIsChecking(false);
            });
    }, [debouncedEmail, currentEmail]);

    return { isChecking, isAvailable, error };
};
